import React, { useMemo, useState } from 'react';
import IconText from '../../icons/IconText';
import { ICON } from '../../../icons/iconIds';
import SelkirkIcon from '../../icons/SelkirkIcon';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformStandardsDriftForChart, 
  getUniqueCRMCodesFromDrift,
  COLORS
} from '../../../utils/qaqc/chartDataTransform';
import {
  augmentRowsZScorePlot,
  Z_SCORE_AXIS_MAX,
  Z_SCORE_AXIS_MIN
} from '../../../utils/qaqc/chartOutliers';

const StandardsDriftChart = ({ 
  data, 
  selectedElement = 'Cu',
  selectedCRM = 'all',
  onElementChange,
  onCRMChange,
  onPointClick,
  height = 500
}) => {
  const [xAxisType, setXAxisType] = useState('index'); // 'index' or 'date'
  
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformStandardsDriftForChart(data, selectedElement, selectedCRM);
  }, [data, selectedElement, selectedCRM]);
  
  // Get unique CRM codes for filter
  const crmCodes = useMemo(() => {
    return getUniqueCRMCodesFromDrift(data, selectedElement);
  }, [data, selectedElement]);
  
  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    
    const zScores = chartData.map(d => d.zScore);
    const mean = zScores.reduce((sum, z) => sum + z, 0) / zScores.length;
    
    const failures = chartData.filter(d => d.status === 'fail').length;
    const warnings = chartData.filter(d => d.status === 'warning').length;
    const passes = chartData.filter(d => d.status === 'pass').length;
    
    const samplesWithFlags = chartData.filter(d => d.hasFlags).length;
    
    return {
      count: chartData.length,
      mean: mean.toFixed(3),
      failures,
      warnings,
      passes,
      passRate: ((passes / chartData.length) * 100).toFixed(1),
      samplesWithFlags
    };
  }, [chartData]);

  const plotChartData = useMemo(
    () => augmentRowsZScorePlot(chartData, 'zScore').map((row) => ({
      ...row,
      ewmaZPlot:
        row.ewmaZ != null && typeof row.ewmaZ === 'number' && !Number.isNaN(row.ewmaZ)
          ? Math.min(Z_SCORE_AXIS_MAX, Math.max(Z_SCORE_AXIS_MIN, row.ewmaZ))
          : row.ewmaZ,
      rollingMeanZ20Plot:
        row.rollingMeanZ20 != null &&
        typeof row.rollingMeanZ20 === 'number' &&
        !Number.isNaN(row.rollingMeanZ20)
          ? Math.min(Z_SCORE_AXIS_MAX, Math.max(Z_SCORE_AXIS_MIN, row.rollingMeanZ20))
          : row.rollingMeanZ20
    })),
    [chartData]
  );
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      
      return (
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          padding: '12px',
          color: '#f9fafb',
          fontSize: '0.875rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
          maxWidth: '300px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#06b6d4' }}>
            {data.sampleNumber}
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#9ca3af', marginBottom: '8px' }}>
            {data.workorderId} | {data.crmCode}
            {data.holeId && ` | ${data.holeId}`}
          </div>
          <div style={{ borderTop: '1px solid #374151', paddingTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Z-Score:</span>
              <span style={{ fontWeight: 'bold', color: data.status === 'fail' ? COLORS.fail : data.status === 'warning' ? COLORS.warning : COLORS.pass }}>
                {data.zScore.toFixed(3)}
              </span>
            </div>
            {data.ewmaZ !== null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
                <span>EWMA:</span>
                <span style={{ fontWeight: 'bold', color: COLORS.mean }}>{data.ewmaZ.toFixed(3)}</span>
              </div>
            )}
            {data.rollingMeanZ20 !== null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
                <span>Rolling Mean (20):</span>
                <span style={{ fontWeight: 'bold', color: COLORS.value }}>{data.rollingMeanZ20.toFixed(3)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Best Value:</span>
              <span style={{ fontWeight: 'bold' }}>{data.bestValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Certified:</span>
              <span style={{ fontWeight: 'bold', color: COLORS.certified }}>{data.certifiedValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
              <span>Status:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: data.status === 'fail' ? COLORS.fail : data.status === 'warning' ? COLORS.warning : COLORS.pass
              }}>
                {data.qcStatus}
              </span>
            </div>
            
            {data.hasFlags && (
              <>
                <div style={{ borderTop: '1px solid #374151', marginTop: '8px', paddingTop: '8px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#f59e0b' }}>
                    <IconText icon={ICON.uiWarning} iconSize={14} as="span">Run-Rule Violations:</IconText>
                  </div>
                  {data.activeFlags.map((flag, i) => (
                    <div key={i} style={{ fontSize: '0.75rem', color: '#fbbf24', marginBottom: '2px' }}>
                      • {flag}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {data.receivedDate && (
            <div style={{ borderTop: '1px solid #374151', marginTop: '8px', paddingTop: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>
              Date: {new Date(data.receivedDate).toLocaleDateString()}
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Custom dot component for scatter points
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (!payload || cx === undefined || cy === undefined) return null;
    
    const getColor = () => {
      if (payload.status === 'fail') return COLORS.fail;
      if (payload.status === 'warning') return COLORS.warning;
      return COLORS.pass;
    };
    
    const handleClick = () => {
      if (onPointClick) {
        onPointClick(payload.raw);
      }
    };
    
    const r = payload.hasFlags ? 7 : 5;
    const clip = payload.zScoreAxisLabel;
    const clipLow =
      clip && payload.zScore < (payload.plotZScore ?? payload.zScore);

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill={getColor()}
          stroke={payload.hasFlags ? '#fbbf24' : 'none'}
          strokeWidth={payload.hasFlags ? 2 : 0}
          style={{ cursor: onPointClick ? 'pointer' : 'default' }}
          onClick={handleClick}
          opacity={0.8}
        />
        {payload.hasFlags && (
          <text
            x={cx}
            y={clip ? (clipLow ? cy - r - 16 : cy - 12) : cy - 12}
            fill="#fbbf24"
            fontSize="12"
            fontWeight="bold"
            textAnchor="middle"
          >
            <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
          </text>
        )}
        {clip && (
          <text
            x={cx}
            y={clipLow ? cy + r + 12 : cy - r - (payload.hasFlags ? 20 : 4)}
            fill="#e5e7eb"
            fontSize={10}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };
  
  return (
    <div className="standards-drift-chart">
      {/* Filter Controls - Always visible */}
      <div style={{ 
        padding: '1rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Element Filter */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Element:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {['Au', 'Ag', 'Cu'].map(element => (
                <button
                  key={element}
                  onClick={() => onElementChange && onElementChange(element)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    background: selectedElement === element ? '#06b6d4' : '#374151',
                    color: selectedElement === element ? '#0c4a6e' : '#f9fafb',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: selectedElement === element ? '600' : '400',
                    cursor: 'pointer'
                  }}
                >
                  {element}
                </button>
              ))}
            </div>
          </div>
          
          {/* CRM Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>CRM:</label>
            <select
              value={selectedCRM}
              onChange={(e) => onCRMChange && onCRMChange(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All CRMs</option>
              {crmCodes.map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          
          {/* X-Axis Toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>X-Axis:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button
                onClick={() => setXAxisType('index')}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: xAxisType === 'index' ? '#06b6d4' : '#374151',
                  color: xAxisType === 'index' ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: xAxisType === 'index' ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Sequence
              </button>
              <button
                onClick={() => setXAxisType('date')}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: xAxisType === 'date' ? '#06b6d4' : '#374151',
                  color: xAxisType === 'date' ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: xAxisType === 'date' ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Date
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Statistics Panel */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '0.75rem',
          padding: '1rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.875rem'
        }}>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Total Samples</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb' }}>{stats.count}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Mean Z-Score</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#06b6d4' }}>{stats.mean}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Pass</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.pass }}>{stats.passes}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Warning</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.warning }}>{stats.warnings}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Fail</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.fail }}>{stats.failures}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Pass Rate</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb' }}>{stats.passRate}%</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Run-Rule Flags</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fbbf24' }}>{stats.samplesWithFlags}</div>
          </div>
        </div>
      )}
      
      {/* Chart */}
      {chartData.length === 0 ? (
        <div style={{ 
          padding: '3rem',
          textAlign: 'center',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          color: '#9ca3af'
        }}>
          <p>No data available for the selected filters.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Try selecting a different element or CRM code.
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart 
            data={plotChartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
            
            <XAxis 
              dataKey={xAxisType === 'index' ? 'index' : 'sampleSeqNum'}
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{ 
                value: xAxisType === 'index' ? 'Sample Sequence' : 'Sample Seq Num', 
                position: 'insideBottom', 
                offset: -10,
                style: { fill: COLORS.text, fontSize: 12 }
              }}
            />
            
            <YAxis
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{
                value: 'Z-Score',
                angle: -90,
                position: 'insideLeft',
                style: { fill: COLORS.text, fontSize: 12 }
              }}
              domain={[Z_SCORE_AXIS_MIN, Z_SCORE_AXIS_MAX]}
              allowDataOverflow
              tickFormatter={(value) => value.toFixed(2)}
            />
            
            {/* Reference lines for control limits */}
            <ReferenceLine y={0} stroke={COLORS.text} strokeDasharray="5 5" strokeWidth={1} />
            <ReferenceLine y={2} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} label={{ value: '+2SD', fill: COLORS.warning, fontSize: 10 }} />
            <ReferenceLine y={-2} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} label={{ value: '-2SD', fill: COLORS.warning, fontSize: 10 }} />
            <ReferenceLine y={3} stroke={COLORS.fail} strokeDasharray="3 3" strokeWidth={1} label={{ value: '+3SD', fill: COLORS.fail, fontSize: 10 }} />
            <ReferenceLine y={-3} stroke={COLORS.fail} strokeDasharray="3 3" strokeWidth={1} label={{ value: '-3SD', fill: COLORS.fail, fontSize: 10 }} />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* EWMA Line */}
            <Line
              type="monotone"
              dataKey="ewmaZPlot"
              stroke={COLORS.mean}
              strokeWidth={2}
              dot={false}
              name="EWMA"
              connectNulls
            />
            
            {/* Rolling Mean Line */}
            <Line
              type="monotone"
              dataKey="rollingMeanZ20Plot"
              stroke={COLORS.value}
              strokeWidth={2}
              dot={false}
              name="Rolling Mean (20)"
              connectNulls
            />
            
            {/* Scatter Points */}
            <Scatter
              dataKey="plotZScore"
              shape={<CustomDot />}
              name="Z-Score"
            />
            
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              content={() => (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '1.5rem',
                  fontSize: '0.875rem',
                  color: '#9ca3af',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.pass }} />
                    Pass (±2SD)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.warning }} />
                    Warning (>2SD)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.fail }} />
                    Fail (>3SD)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '16px', height: '3px', background: COLORS.mean }} />
                    EWMA
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '16px', height: '3px', background: COLORS.value }} />
                    Rolling Mean
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
                    Run-Rule Flag
                  </span>
                </div>
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      
      {/* Helper text */}
      {onPointClick && chartData.length > 0 && (
        <div style={{ 
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '6px',
          fontSize: '0.875rem', 
          color: '#9ca3af', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          <SelkirkIcon name={ICON.uiPointerHint} size={20} decorative />
          <span>
            <strong style={{ color: '#06b6d4' }}>Click any data point</strong> to investigate. Points with <SelkirkIcon name={ICON.uiWarning} size={14} decorative /> have run-rule violations.
          </span>
        </div>
      )}
    </div>
  );
};

export default StandardsDriftChart;
